import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Trash2,
  Edit2,
  Save,
  X,
  FileText,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  FolderOpen,
  PlusCircle,
  Link2,
  Download,
  RefreshCw,
  HomeIcon,
} from 'lucide-react'
import { documentService } from '@/services/documentService'
import type { Document, DocumentType, ProductType } from '@/types'
import { exportDocumentService, ProjectDetail, ProjectWithDocuments, ProjectFormData } from '@/services/exportDocumentService'

interface AdminPanelProps {
  onClose?: () => void
}

interface DocumentFormState {
  name: string
  type: DocumentType
  description: string
  url: string
  productType: ProductType
  sortOrder: number
  active: boolean
}

const ADMIN_PASSWORD = 'admin123'
const AUTH_STORAGE_KEY = 'admin_panel_authenticated'
const DOCUMENT_TYPE_OPTIONS: Array<{ value: DocumentType; label: string }> = [
  { value: 'TDS', label: 'TDS' },
  { value: 'ESR', label: 'ESR' },
  { value: 'MSDS', label: 'MSDS' },
  { value: 'LEED', label: 'LEED' },
  { value: 'Installation', label: 'Installation' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'Acoustic', label: 'Acoustic' },
  { value: 'PartSpec', label: 'Part Spec' },
]

const defaultDocumentForm: DocumentFormState = {
  name: '',
  type: 'TDS',
  description: '',
  url: '',
  productType: 'structural-floor',
  sortOrder: 1,
  active: true,
}

// Safely format an encoded filename (e.g. with %20, %C2%AE, etc.) for display only.
// This does NOT change the underlying URL used for the Open PDF button.
const formatFilename = (filename?: string | null): string => {
  if (!filename) return ''
  try {
    return decodeURIComponent(filename)
  } catch {
    // Fallback: at minimum, show spaces instead of %20 if decode fails
    return filename.replace(/%20/g, ' ')
  }
}

export default function AdminPanel({ onClose }: AdminPanelProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [editingDoc, setEditingDoc] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<Document>>({})
  const [newDocument, setNewDocument] = useState<DocumentFormState>(defaultDocumentForm)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [downloadedPacket, setDownloadedPacket] = useState<ProjectDetail[]>();
  const [isUsersGenerateModalOpen, setIsUsersGenerateModalOpen] = useState(false);
  const [projectsGrouped, setProjectsGrouped] = useState<ProjectWithDocuments[]>([]);

  // Restore authentication state from localStorage on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem(AUTH_STORAGE_KEY)
    if (savedAuth === 'true') {
      setIsAuthenticated(true)
    }
  }, [])

  // Function to group projects by project_id
  const groupProjectsByProjectId = (data: ProjectDetail[]): ProjectWithDocuments[] => {
    const projectMap = new Map<number, ProjectWithDocuments>();

    data.forEach((item) => {
      // In JOIN result from SELECT *:
      // - item.id = project_details.id (unique project ID: 1, 2, 3, 4, 5)
      // - item.project_id = documents.project_id (foreign key, repeats: 1,1,1,2,2,2,3,3,3,4,4,4,5,5,5)
      // We group by project_details.id (item.id) to get unique projects
      const projectId = item.id; // This is the unique project_details.id
      
      // Only create project entry if it doesn't exist
      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          id: projectId, // project_details.id (unique: 1, 2, 3, 4, 5)
          project_name: item.project_name,
          product_type: item.product_type || '',
          submitted_to: item.submitted_to,
          project_number: item.project_number || '',
          prepared_by: item.prepared_by,
          email: item.email,
          phone_number: item.phone_number,
          date: item.date,
          status: item.status,
          product_size: item.product_size || '',
          documents: []
        });
      }

      // Get the project we just created or already exists
      const project = projectMap.get(projectId)!;
      
      // Add document if it exists in this row
      // item.project_id should match projectId (both refer to the same project)
      if (item.name && item.project_id && item.project_id === projectId) {
        // Check if document already exists to avoid duplicates
        // Use name, type, and filename as unique identifier for document
        const docExists = project.documents.some(doc => 
          doc.name === item.name && 
          doc.type === item.type && 
          doc.filename === item.filename
        );
        
        if (!docExists) {
          project.documents.push({
            id: item.project_id, // Use project_id as document identifier
            name: item.name,
            description: item.description || '',
            filename: item.filename || '',
            url: item.url || '',
            type: item.type || '',
            sort_order: item.sort_order || 0,
            active: item.active !== undefined ? item.active : true
          });
        }
      }
    });

    // Return only unique projects (should be 5 projects, not 15)
    return Array.from(projectMap.values());
  };

  // get all project from d1
  useEffect(() => {
    const fetchProjectDetail = async () => {
      const response = await exportDocumentService.getProjectDetail();
      setDownloadedPacket(response);
      const grouped = groupProjectsByProjectId(response);
      setProjectsGrouped(grouped);
    };
    fetchProjectDetail();
  }, []);

  // Refresh projects when modal is opened
  useEffect(() => {
    if (isUsersGenerateModalOpen) {
      const fetchProjectDetail = async () => {
        try {
          const response = await exportDocumentService.getProjectDetail();
          const grouped = groupProjectsByProjectId(response);
          setProjectsGrouped(grouped);
          setDownloadedPacket(response);
        } catch (err) {
          setError('Failed to load projects');
        }
      };
      fetchProjectDetail();
    }
  }, [isUsersGenerateModalOpen]);


  useEffect(() => {
    if (!isAuthenticated) return
    let unsubscribe: (() => void) | undefined
    loadDocuments()
    unsubscribe = documentService.subscribe((docs) => {
      setDocuments([...docs].sort((a, b) => a.sortOrder - b.sortOrder))
    })
    return () => {
      unsubscribe?.()
    }
  }, [isAuthenticated])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const docs = await documentService.getAllDocuments({ includeInactive: true })
      setDocuments([...docs].sort((a, b) => a.sortOrder - b.sortOrder))
    } catch (err) {
      setError('Failed to load documents')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true)
      localStorage.setItem(AUTH_STORAGE_KEY, 'true')
      setError(null)
      setPassword('') // Clear password after successful login
    } else {
      setError('Invalid password')
    }
  }

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newDocument.name.trim()) {
      setError('Document name is required')
      return
    }
    if (!newDocument.url.trim()) {
      setError('Document URL is required')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await documentService.createDocument({
        ...newDocument,
        sortOrder: Number(newDocument.sortOrder),
        description: newDocument.description.trim(),
        url: newDocument.url.trim(),
      })
      setSuccess('Document added successfully. Remember to upload the PDF to /docs on Netlify.')
      setNewDocument(defaultDocumentForm)
      await loadDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create document')
      console.error(err)
    } finally {
      setLoading(false)
      setTimeout(() => {
        setSuccess(null)
        setError(null)
      }, 5000)
    }
  }

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return

    try {
      setLoading(true)
      await documentService.deleteDocument(docId)
      setSuccess('Document deleted successfully')
      await loadDocuments()
    } catch (err) {
      setError('Failed to delete document')
      console.error(err)
    } finally {
      setLoading(false)
      setTimeout(() => setSuccess(null), 3000)
    }
  }

  const handleExportDocuments = async () => {
    try {
      await documentService.exportDocuments()
    } catch (err) {
      setError('Failed to export documents')
      console.error(err)
    }
  }

  const handleReloadFromSource = async () => {
    try {
      setLoading(true)
      await documentService.reloadFromRemote()
      setSuccess('Metadata reloaded from documents.json')
      await loadDocuments()
    } catch (err) {
      setError('Failed to reload documents from source')
      console.error(err)
    } finally {
      setLoading(false)
      setTimeout(() => setSuccess(null), 3000)
    }
  }

  const handleExportDownloadedDocuments = (type: string) => {
    if(downloadedPacket) {
      if(type === 'csv') {
        exportDocumentService.exportDocuments(downloadedPacket);
      }
    }
  }

  const handleOpenUsersGenerateModal = () => {
    setIsUsersGenerateModalOpen(true);
  }

  const handleCloseUsersGenerateModal = () => {
    setIsUsersGenerateModalOpen(false);
  }

  const handleDeleteProject = async (projectId: number) => {
    if (!confirm('Are you sure you want to delete this project? This will also delete all associated documents.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await exportDocumentService.deleteProject(projectId);
      setSuccess('Project deleted successfully');
      
      // Refresh projects
      const response = await exportDocumentService.getProjectDetail();
      const grouped = groupProjectsByProjectId(response);
      setProjectsGrouped(grouped);
      setDownloadedPacket(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
    }
  }

  const handleResetLocalOverride = async () => {
    documentService.clearLocalCache()
    await loadDocuments()
    setSuccess('Local overrides cleared and metadata refreshed.')
    setTimeout(() => setSuccess(null), 3000)
  }

  const handleEditDocument = (doc: Document) => {
    setEditingDoc(doc.id)
    setEditForm(doc)
  }

  const handleSaveEdit = async () => {
    if (!editingDoc || !editForm) return

    if (!editForm.name || editForm.name.trim() === '') {
      setError('Document name is required')
      return
    }

    if (!editForm.type) {
      setError('Document type is required')
      return
    }

    if (!editForm.url || editForm.url.trim() === '') {
      setError('Document URL is required')
      return
    }

    if (!editForm.productType) {
      setError('Product category is required')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await documentService.updateDocument(editingDoc, {
        ...editForm,
        sortOrder: Number(editForm.sortOrder ?? 1),
        url: editForm.url.trim(),
        description: editForm.description?.trim() ?? '',
      })
      setSuccess('Document updated successfully')
      setEditingDoc(null)
      setEditForm({})
      await loadDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update document')
      console.error(err)
    } finally {
      setLoading(false)
      setTimeout(() => {
        setSuccess(null)
        setError(null)
      }, 3000)
    }
  }

  const handleCancelEdit = () => {
    setEditingDoc(null)
    setEditForm({})
  }

  // Filter documents by category
  const structuralFloorDocs = documents
    .filter(doc => doc.productType === 'structural-floor')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    console.log('structuralFloorDocs: ', structuralFloorDocs);
    const underlaymentDocs = documents
    .filter(doc => doc.productType === 'underlayment')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    console.log('underlaymentDocs: ', underlaymentDocs);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4 relative">
        <button
          type="button"
          onClick={() => window.location.href = '/'}
          className="text-xl absolute top-4 left-4 text-white hover:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center z-10"
        >
          <HomeIcon className="inline-block w-8 h-8 mr-2" />
          Back to Home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card dark:glass-card-dark p-8 max-w-md w-full"
        >
          <div className="flex items-center justify-center mb-6">
            <Lock className="w-12 h-12 text-blue-500" />
          </div>
          <h1 className="text-2xl font-bold text-center mb-6 text-white">
            Admin Access
          </h1>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              <Unlock className="inline-block w-5 h-5 mr-2" />
              Login
            </button>
          </form>

          {onClose && (
            <button
              onClick={onClose}
              className="w-full mt-4 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium py-2"
            >
              Back to App
            </button>
          )}
        </motion.div>
      </div>
    )
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Document Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage centralized document metadata and Netlify URLs
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsAuthenticated(false)
                localStorage.removeItem(AUTH_STORAGE_KEY)
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <Lock className="inline-block w-5 h-5 mr-2" />
              Logout
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                <X className="inline-block w-5 h-5 mr-2" />
                Close
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg whitespace-pre-line"
            >
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg"
            >
              {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Download PDF */}
        <div className='glass-card dark:glass-card-dark p-6 mb-8'>
          <div className='flex flex-warp items-center justify-between gap-4 mb-6'>
            <div>
              <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
                Information Downloaded Packet
              </h2>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                Give information about project
              </p>
            </div>
            <div className='flex flex-warp gap-2'>
              <button
                type='button'
                onClick={handleOpenUsersGenerateModal}
                className='px-3 py-2 text-sm rounded-lg text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2'
              >
                <PlusCircle className="w-4 h-4" />
                Users Generate
              </button>
              <button
                type='button'
                onClick={() => handleExportDownloadedDocuments('csv') }
                className={
                  `px-3 py-2 text-sm rounded-lg text-gray-700 dark:text-gray-200 ${downloadedPacket?.length != 0 ? 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700' : 'bg-gray-200'} transition-colors flex items-center gap-2`
                }
                disabled={downloadedPacket?.length == 0}
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>

        {/* Metadata Management */}
        <div className="glass-card dark:glass-card-dark p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Document Metadata
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Point each record to a PDF hosted in Netlify&apos;s <code>/docs</code> folder.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleExportDocuments}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download JSON
              </button>
              <button
                type="button"
                onClick={handleReloadFromSource}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload from /docs
              </button>
              <button
                type="button"
                onClick={handleResetLocalOverride}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4 rotate-180" />
                Clear Local Cache
              </button>
            </div>
          </div>

          <form onSubmit={handleCreateDocument} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Document Name
                </label>
                <input
                  type="text"
                  value={newDocument.name}
                  onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                  placeholder="e.g. Technical Data Sheet"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Document Type
                </label>
                <select
                  value={newDocument.type}
                  onChange={(e) =>
                    setNewDocument({ ...newDocument, type: e.target.value as DocumentType })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                >
                  {DOCUMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Product Category
                </label>
                <select
                  value={newDocument.productType}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      productType: e.target.value as ProductType,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                >
                  <option value="structural-floor">Structural Floor</option>
                  <option value="underlayment">Underlayment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Sort Order
                </label>
                <input
                  type="number"
                  min={1}
                  value={newDocument.sortOrder}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      sortOrder: Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  PDF URL
                </label>
                <div className="relative">
                  <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="url"
                    value={newDocument.url}
                    onChange={(e) => setNewDocument({ ...newDocument, url: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                    placeholder="/docs/technical-data-sheet.pdf"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="new-doc-active"
                  type="checkbox"
                  checked={newDocument.active}
                  onChange={(e) => setNewDocument({ ...newDocument, active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="new-doc-active" className="text-sm text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={newDocument.description}
                onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                placeholder="Short description shown in the selector"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={loading}
              >
                <PlusCircle className="w-4 h-4" />
                Add Document
              </button>
              <button
                type="button"
                onClick={() => setNewDocument(defaultDocumentForm)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Reset Form
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Upload or replace the actual PDF files directly in the Netlify dashboard (folder
              <code className="mx-1">/docs</code>). Updating the metadata here keeps the URLs stable for the worker.
            </p>
          </form>
        </div>

        {/* Documents by Category */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Structural Floor Documents */}
          <DocumentCategory
            title="Structural Floor Documents"
            color="green"
            documents={structuralFloorDocs}
            loading={loading}
            editingDoc={editingDoc}
            editForm={editForm}
            onEdit={handleEditDocument}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onDelete={handleDeleteDocument}
            onEditFormChange={setEditForm}
          />

          {/* Underlayment Documents */}
          <DocumentCategory
            title="Underlayment Documents"
            color="purple"
            documents={underlaymentDocs}
            loading={loading}
            editingDoc={editingDoc}
            editForm={editForm}
            onEdit={handleEditDocument}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onDelete={handleDeleteDocument}
            onEditFormChange={setEditForm}
          />
        </div>
      </div>

      {/* Users Generate Modal */}
      <AnimatePresence>
        {isUsersGenerateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={handleCloseUsersGenerateModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Users Generate
                </h2>
                <button
                  onClick={handleCloseUsersGenerateModal}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg"
                  >
                    {error}
                  </motion.div>
                )}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg"
                  >
                    {success}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Projects List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Projects List ({projectsGrouped.length})
                </h3>
                {projectsGrouped.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No projects found</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {projectsGrouped.map((project) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {project.project_name}
                              </h4>
                              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                {project.product_type || 'N/A'}
                              </span>
                              <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded">
                                {project.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                              <div>
                                <span className="font-medium">Submitted To:</span> {project.submitted_to}
                              </div>
                              <div>
                                <span className="font-medium">Prepared By:</span> {project.prepared_by}
                              </div>
                              <div>
                                <span className="font-medium">Email:</span> {project.email}
                              </div>
                              <div>
                                <span className="font-medium">Date:</span> {project.date}
                              </div>
                            </div>
                            {project.documents && project.documents.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Documents ({project.documents.length}):
                                </p>
                                <div className="space-y-1">
                                  {project.documents.map((doc, idx) => (
                                    <div key={idx} className="text-xs text-gray-500 dark:text-gray-400 pl-4">
                                      • {doc.name} ({doc.type})
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleDeleteProject(project.id)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Delete Project"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Document Category Component
interface DocumentCategoryProps {
  title: string
  color: 'green' | 'purple'
  documents: Document[]
  loading: boolean
  editingDoc: string | null
  editForm: Partial<Document>
  onEdit: (doc: Document) => void
  onSave: () => void
  onCancel: () => void
  onDelete: (id: string) => void
  onEditFormChange: (form: Partial<Document>) => void
}

function DocumentCategory({
  title,
  color,
  documents,
  loading,
  editingDoc,
  editForm,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onEditFormChange
}: DocumentCategoryProps) {
  const colorClasses = {
    green: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-300 dark:border-green-700'
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-700 dark:text-purple-300',
      border: 'border-purple-300 dark:border-purple-700'
    }
  }

  const colors = colorClasses[color]

  return (
    <div className="glass-card dark:glass-card-dark p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {documents.length} {documents.length === 1 ? 'document' : 'documents'}
            </p>
          </div>
        </div>
      </div>

      {loading && documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No documents in this category yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <motion.div
              key={doc.id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`border ${colors.border} rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}
            >
              {editingDoc === doc.id ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Document Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => onEditFormChange({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Document Type
                      </label>
                      <select
                        value={editForm.type || ''}
                        onChange={(e) =>
                          onEditFormChange({ ...editForm, type: e.target.value as DocumentType })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                      >
                        {DOCUMENT_TYPE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                      PDF URL
                    </label>
                    <input
                      type="url"
                      value={editForm.url || ''}
                      onChange={(e) => onEditFormChange({ ...editForm, url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Sort Order
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={editForm.sortOrder ?? 1}
                        onChange={(e) =>
                          onEditFormChange({
                            ...editForm,
                            sortOrder: Number(e.target.value),
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                        Product Category
                      </label>
                      <select
                        value={editForm.productType || 'structural-floor'}
                        onChange={(e) =>
                          onEditFormChange({
                            ...editForm,
                            productType: e.target.value as ProductType,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                      >
                        <option value="structural-floor">Structural Floor</option>
                        <option value="underlayment">Underlayment</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        id={`doc-active-${doc.id}`}
                        type="checkbox"
                        checked={editForm.active ?? true}
                        onChange={(e) =>
                          onEditFormChange({
                            ...editForm,
                            active: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`doc-active-${doc.id}`}
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) =>
                        onEditFormChange({ ...editForm, description: e.target.value })
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={onSave}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <Save className="inline-block w-4 h-4 mr-2" />
                      Save
                    </button>
                    <button
                      onClick={onCancel}
                      className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      <X className="inline-block w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <FileText className="w-8 h-8 text-blue-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {doc.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {doc.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <span className={`px-2 py-1 ${colors.bg} ${colors.text} rounded`}>
                          {doc.type}
                        </span>
                        <span className="font-medium">
                          Order #{doc.sortOrder ?? 0}
                        </span>
                        <span
                          className={doc.active ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
                        >
                          {doc.active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="capitalize">
                          {doc.productType === 'structural-floor' ? 'Structural Floor' : 'Underlayment'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-500 mb-2">
                        {doc.filename && (
                          <span className="truncate max-w-xs">
                            {formatFilename(doc.filename)}
                          </span>
                        )}
                        {typeof doc.size === 'number' && (
                          <span>{(doc.size / 1024 / 1024).toFixed(2)} MB</span>
                        )}
                      </div>
                      {doc.url && (
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <Link2 className="w-4 h-4" />
                          Open PDF
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(doc)}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(doc.id)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}
